import { API_URL, MONITORING_API_URL } from '../config/connect';
import { getDeviceInfo } from '../utils/deviceInfo';

// Event types enum
export const MonitoringEventType = {
  pageView: 'pageView',
  search: 'search',
  categoryView: 'categoryView',
  apiRequestSuccess: 'apiRequestSuccess',
  apiRequestFailure: 'apiRequestFailure',
  login: 'login',
  logout: 'logout',
  registration: 'registration',
  advertise: 'advertise',
  balanceTopUp: 'balanceTopUp',
  appOpen: 'appOpen',
  appClose: 'appClose',
};

// Storage keys
const STORAGE_KEYS = {
  QUEUE: 'monitoring_events_queue',
  USER_ID: 'monitoring_user_id',
  PHONE_NUMBER: 'monitoring_phone_number',
  DEVICE_ID: 'monitoring_device_id',
};

// Maximum events to store in queue
const MAX_QUEUE_SIZE = 100;

// Batch size for sending events
const BATCH_SIZE = 10;

class MonitoringService {
  constructor() {
    this.deviceId = null;
    this.userId = null;
    this.phoneNumber = null;
    this.isOnline = navigator.onLine;
    this.isInitialized = false;
    this.sendQueueInterval = null;
    
    // Initialize device ID
    this.initializeDeviceId();
    
    // Load user info from storage
    this.loadUserInfo();
    
    // Setup online/offline listeners
    this.setupNetworkListeners();
    
    // Setup queue processing
    this.setupQueueProcessing();
  }

  async initializeDeviceId() {
    try {
      const deviceInfo = await getDeviceInfo();
      this.deviceId = deviceInfo.device_id;
      localStorage.setItem(STORAGE_KEYS.DEVICE_ID, this.deviceId);
    } catch (error) {
      console.warn('Failed to initialize device ID:', error);
      // Fallback to localStorage or generate new
      this.deviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID) || 
        `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(STORAGE_KEYS.DEVICE_ID, this.deviceId);
    }
  }

  loadUserInfo() {
    this.userId = localStorage.getItem(STORAGE_KEYS.USER_ID) || null;
    this.phoneNumber = localStorage.getItem(STORAGE_KEYS.PHONE_NUMBER) || null;
    
    // Try to get phone number from user info if available
    if (!this.phoneNumber) {
      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        // Try to get from API if needed
        this.fetchUserPhoneNumber();
      }
    }
  }

  async fetchUserPhoneNumber() {
    try {
      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) return;

      const response = await fetch(`${API_URL}/api/v1/user/profile`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.user?.phone_number) {
          this.setPhoneNumber(data.user.phone_number);
        }
        if (data?.user?.id) {
          this.setUserId(data.user.id.toString());
        }
      }
    } catch (error) {
      // Failed to fetch user phone number
    }
  }

  setupNetworkListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  setupQueueProcessing() {
    // Process queue every 30 seconds
    this.sendQueueInterval = setInterval(() => {
      if (this.isOnline) {
        this.processQueue();
      }
    }, 30000);

    // Process queue on page visibility change
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.isOnline) {
        this.processQueue();
      }
    });
  }

  setUserId(userId) {
    this.userId = userId;
    if (userId) {
      localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
    } else {
      localStorage.removeItem(STORAGE_KEYS.USER_ID);
    }
  }

  setPhoneNumber(phoneNumber) {
    this.phoneNumber = phoneNumber;
    if (phoneNumber) {
      localStorage.setItem(STORAGE_KEYS.PHONE_NUMBER, phoneNumber);
    } else {
      localStorage.removeItem(STORAGE_KEYS.PHONE_NUMBER);
    }
  }

  getPhoneNumber() {
    // Return phone number or default to empty string
    return this.phoneNumber || '';
  }

  getQueue() {
    try {
      const queueStr = localStorage.getItem(STORAGE_KEYS.QUEUE);
      return queueStr ? JSON.parse(queueStr) : [];
    } catch (error) {
      return [];
    }
  }

  saveQueue(queue) {
    try {
      // Limit queue size
      const limitedQueue = queue.slice(-MAX_QUEUE_SIZE);
      localStorage.setItem(STORAGE_KEYS.QUEUE, JSON.stringify(limitedQueue));
    } catch (error) {
      // If storage is full, try to clear old events
      try {
        const halfQueue = queue.slice(-Math.floor(MAX_QUEUE_SIZE / 2));
        localStorage.setItem(STORAGE_KEYS.QUEUE, JSON.stringify(halfQueue));
      } catch (e) {
        // Failed to save reduced queue
      }
    }
  }

  addToQueue(event) {
    const queue = this.getQueue();
    queue.push(event);
    this.saveQueue(queue);
  }

  async processQueue() {
    const queue = this.getQueue();
    if (queue.length === 0 || !this.isOnline) {
      return;
    }

    // Process in batches
    const batches = [];
    for (let i = 0; i < queue.length; i += BATCH_SIZE) {
      batches.push(queue.slice(i, i + BATCH_SIZE));
    }

    for (const batch of batches) {
      try {
        await this.sendBatch(batch);
        // Remove sent events from queue
        const remainingQueue = queue.slice(batches.indexOf(batch) * BATCH_SIZE + batch.length);
        this.saveQueue(remainingQueue);
      } catch (error) {
        // Silently handle 422 errors (validation errors) - likely data format issues
        // Continue processing other batches
        const errorMessage = error?.message || '';
        if (errorMessage.includes('422')) {
          // Remove invalid batch from queue
          const batchIndex = batches.indexOf(batch);
          const beforeBatch = queue.slice(0, batchIndex * BATCH_SIZE);
          const afterBatch = queue.slice(batchIndex * BATCH_SIZE + batch.length);
          this.saveQueue([...beforeBatch, ...afterBatch]);
        } else {
          // Stop processing for other errors
          break;
        }
      }
    }
  }

  async sendEvent(event) {
    // Ensure device ID is set
    if (!this.deviceId) {
      await this.initializeDeviceId();
    }

    // Add required fields
    const enrichedEvent = {
      ...event,
      timestamp: event.timestamp || new Date().toISOString(),
      deviceId: this.deviceId,
      phone_number: this.getPhoneNumber(),
      phoneNumber: this.getPhoneNumber(), // Backend requires camelCase
      userId: this.userId || undefined,
    };

    // Don't track monitoring events themselves
    if (event.apiEndpoint && event.apiEndpoint.includes('/monitoring/events')) {
      return;
    }

    if (this.isOnline) {
      try {
        await this.sendSingleEvent(enrichedEvent);
      } catch (error) {
        this.addToQueue(enrichedEvent);
      }
    } else {
      this.addToQueue(enrichedEvent);
    }
  }

  async sendSingleEvent(event) {
    const response = await fetch(`${MONITORING_API_URL}/monitoring/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
    });

    if (!response.ok) {
      throw new Error(`Failed to send event: ${response.status}`);
    }
  }

  async sendBatch(events) {
    const response = await fetch(`${MONITORING_API_URL}/monitoring/events/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ events }),
    });

    if (!response.ok) {
      // Try to get error details from response
      let errorMessage = `Failed to send batch: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData?.message || errorData?.error) {
          errorMessage = `${errorMessage} - ${errorData.message || errorData.error}`;
        }
      } catch (e) {
        // Ignore JSON parse errors
      }
      throw new Error(errorMessage);
    }
  }

  // Convenience methods for different event types
  async trackPageView(pageName, data = {}) {
    await this.sendEvent({
      eventType: MonitoringEventType.pageView,
      eventName: 'page_view',
      pageName,
      data,
    });
  }

  async trackSearch(query, categoryId = null) {
    await this.sendEvent({
      eventType: MonitoringEventType.search,
      eventName: 'search',
      searchQuery: query,
      categoryId: categoryId || undefined,
    });
  }

  async trackCategoryView(categoryId, categoryName = null) {
    await this.sendEvent({
      eventType: MonitoringEventType.categoryView,
      eventName: 'category_view',
      categoryId,
      data: categoryName ? { categoryName } : {},
    });
  }

  async trackApiRequestSuccess(endpoint, method, statusCode) {
    await this.sendEvent({
      eventType: MonitoringEventType.apiRequestSuccess,
      eventName: 'api_request_success',
      apiEndpoint: endpoint,
      httpMethod: method,
      statusCode,
    });
  }

  async trackApiRequestFailure(endpoint, method, statusCode, errorMessage) {
    await this.sendEvent({
      eventType: MonitoringEventType.apiRequestFailure,
      eventName: 'api_request_failure',
      apiEndpoint: endpoint,
      httpMethod: method,
      statusCode,
      errorMessage,
    });
  }

  async trackLogin(phoneNumber, success, errorMessage = null) {
    // Always set phone number temporarily for this event
    const previousPhoneNumber = this.phoneNumber;
    if (phoneNumber) {
      this.phoneNumber = phoneNumber;
    }

    if (success && phoneNumber) {
      this.setPhoneNumber(phoneNumber);
    }

    await this.sendEvent({
      eventType: MonitoringEventType.login,
      eventName: 'login',
      success,
      error: errorMessage || undefined,
    });

    // Restore previous phone number if login failed and we had one
    if (!success && previousPhoneNumber) {
      this.phoneNumber = previousPhoneNumber;
    }
  }

  async trackLogout() {
    await this.sendEvent({
      eventType: MonitoringEventType.logout,
      eventName: 'logout',
    });
    this.setUserId(null);
    this.setPhoneNumber(null);
  }

  async trackRegistration(phoneNumber, success, errorMessage = null) {
    if (success && phoneNumber) {
      this.setPhoneNumber(phoneNumber);
    }
    await this.sendEvent({
      eventType: MonitoringEventType.registration,
      eventName: 'registration',
      success,
      error: errorMessage || undefined,
    });
  }

  async trackAdvertise(adId, success, errorMessage = null, data = {}) {
    await this.sendEvent({
      eventType: MonitoringEventType.advertise,
      eventName: 'advertise',
      success,
      error: errorMessage || undefined,
      data: {
        adId,
        ...data,
      },
    });
  }

  async trackBalanceTopUp(amount, currency, success = true, errorMessage = null) {
    await this.sendEvent({
      eventType: MonitoringEventType.balanceTopUp,
      eventName: 'balance_top_up',
      amount,
      currency,
      success,
      error: errorMessage || undefined,
    });
  }

  async trackAppOpen() {
    await this.sendEvent({
      eventType: MonitoringEventType.appOpen,
      eventName: 'app_open',
    });
  }

  async trackAppClose() {
    await this.sendEvent({
      eventType: MonitoringEventType.appClose,
      eventName: 'app_close',
    });
  }

  // Cleanup
  destroy() {
    if (this.sendQueueInterval) {
      clearInterval(this.sendQueueInterval);
    }
  }
}

// Singleton instance
const monitoringService = new MonitoringService();

export default monitoringService;

